import type { Candidate, Mark, Participant, ResponseRow } from '../types'
import type { BookingMap } from '../api'
import { tally, bestCandidateIds } from '../lib/aggregate'
import { MARK_LABEL } from '../lib/marks'

const MARK_COLOR: Record<Mark, string> = {
  ok: 'text-ok',
  maybe: 'text-maybe',
  no: 'text-no/60',
}

/** 集計表：日程＝行、参加者＝列、右端に○/△/×人数 */
export default function Grid({
  candidates,
  participants,
  responses,
  bookings,
  onEdit,
  onDelete,
}: {
  candidates: Candidate[]
  participants: Participant[]
  responses: ResponseRow[]
  bookings?: BookingMap
  onEdit: (p: Participant) => void
  onDelete: (p: Participant) => void
}) {
  const candidateIds = candidates.map((c) => c.id)
  const tallies = tally(candidateIds, responses)
  const best = bestCandidateIds(tallies)
  const tallyById = new Map(tallies.map((t) => [t.candidateId, t]))

  const marks = new Map<string, Map<string, Mark>>()
  for (const r of responses) {
    if (!marks.has(r.candidate_id)) marks.set(r.candidate_id, new Map())
    marks.get(r.candidate_id)!.set(r.participant_id, r.mark)
  }

  const th = 'border-b border-line px-3 py-2.5 font-semibold whitespace-nowrap'
  const td = 'border-b border-line px-3 py-2.5'

  return (
    <div className="scroll-x-hint overflow-x-auto rounded-2xl border border-line bg-surface">
      <table className="min-w-full border-collapse text-sm">
        <thead>
          <tr className="bg-paper/70 text-muted">
            <th className={`sticky left-0 z-10 bg-paper/95 text-left ${th}`}>日程</th>
            {participants.map((p) => (
              <th key={p.id} className={`text-center ${th}`}>
                <button
                  onClick={() => onEdit(p)}
                  className="font-semibold text-ink transition hover:text-brand"
                >
                  {p.name}
                </button>
                <button
                  onClick={() => onDelete(p)}
                  className="ml-1 text-xs text-muted/40 transition hover:text-conflict"
                  aria-label={`${p.name} を削除`}
                >
                  ✕
                </button>
              </th>
            ))}
            <th className={`text-center text-ok ${th}`}>○</th>
            <th className={`text-center text-maybe ${th}`}>△</th>
            <th className={`text-center text-no ${th}`}>✕</th>
          </tr>
        </thead>
        <tbody>
          {participants.length === 0 && (
            <tr>
              <td colSpan={candidates.length + 4} className="px-4 py-8 text-center text-muted">
                まだ回答がありません。最初の出欠を入力してみましょう。
              </td>
            </tr>
          )}
          {candidates.map((c) => {
            const t = tallyById.get(c.id)
            const isBest = best.has(c.id)
            return (
              <tr key={c.id} className={isBest ? 'bg-teal-soft/40' : ''}>
                <td className={`sticky left-0 z-10 font-semibold text-ink whitespace-nowrap ${td} ${isBest ? 'bg-[#e9f7f4]' : 'bg-surface'}`}>
                  {isBest && <span className="mr-1 text-teal">★</span>}
                  {c.label}
                </td>
                {participants.map((p) => {
                  const m = marks.get(c.id)?.get(p.id)
                  // ○のみ。懸念度（逃げ道の少なさ）が閾値超のときだけ、寒色背景＋件数で警告
                  const b = m === 'ok' ? bookings?.[c.id]?.[p.id] : undefined
                  // 候補になりそうな日（本命度0.5以上）かつ懸念度が閾値超のときだけ警告
                  const flag = !!b && b.leadingness >= 0.5 && b.concern > 0.12
                  // 他の予定に流れる可能性（= 1 − 参加の確度）
                  const risk = b ? Math.round((1 - b.confidence) * 100) : 0
                  const bg = flag
                    ? { backgroundColor: `rgba(108,122,178,${(0.12 + 0.4 * b!.concern).toFixed(3)})` }
                    : undefined
                  const title = flag
                    ? `${p.name}：他の予定に流れる可能性 ${risk}%（参加の確度 ${100 - risk}%）`
                    : undefined
                  return (
                    <td key={p.id} className={`text-center text-lg ${td}`} style={bg} title={title}>
                      <span className={m ? MARK_COLOR[m] : 'text-line'}>{m ? MARK_LABEL[m] : '·'}</span>
                      {flag && (
                        <span className="ml-0.5 align-middle text-xs font-semibold text-muted">
                          {risk}%
                        </span>
                      )}
                    </td>
                  )
                })}
                <td className={`text-center font-display ${td} ${isBest ? 'font-bold text-teal' : 'text-ink'}`}>
                  {t?.ok ?? 0}
                </td>
                <td className={`text-center font-display text-muted ${td}`}>{t?.maybe ?? 0}</td>
                <td className={`text-center font-display text-muted ${td}`}>{t?.no ?? 0}</td>
              </tr>
            )
          })}

          {participants.some((p) => p.comment) && (
            <tr>
              <td className={`sticky left-0 z-10 bg-surface font-semibold text-muted ${td} border-b-0`}>
                コメント
              </td>
              {participants.map((p) => (
                <td
                  key={p.id}
                  className={`align-top text-xs text-muted whitespace-pre-wrap ${td} border-b-0`}
                >
                  {p.comment ?? ''}
                </td>
              ))}
              <td className="border-b-0" colSpan={3} />
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
